import { Dialog, Transition } from "@headlessui/react"
import { Fragment, useEffect, useState } from "react"
import DatePickerInput from 'components/DateInput'
import { useAuth } from "context/AuthContext"
import { reactivateMembershipClient } from "services/memberships-client"
import Translation from "components/Translation"
import { getTranslation } from "utils/language-helper"
import { useAppContext } from "context/AppContext"
import { GlobalDialogType } from "components/Modals/GlobalDialog"
import { useAppErrorApiContext } from "context/AppErrorContext"

export interface MembershipReactivateModalProps {
    is_open: boolean
    onConfirm(): void
    onCancel(): void
}

export function MembershipReactivateModal ({
    is_open: modal_open,
    onConfirm = (): void => {},
    onCancel = (): void => {},
}: MembershipReactivateModalProps) {
    const { setGlobalError } = useAppErrorApiContext()
    const { setGlobalLoading, translations } = useAppContext()
    const { MembershipEditModal, GlobalDialog } = useAuth()
    const { attributes } = MembershipEditModal
    const [is_open, setOpen] = useState<boolean>(false)

    const [show_date_picker, toggleDatePicker] = useState(false)
    const is_package = attributes?.type == 'package'

    async function onConfirmModal(renewal_date?: Date) {
        if (!renewal_date) {
            renewal_date = new Date()
            renewal_date.setTime(renewal_date.getTime() + (5*60*1000))
        }
        try {
            setGlobalLoading(true)
            let api = await reactivateMembershipClient(attributes?.id as string, renewal_date.toISOString())
            if (api.ok) {
                setOpen(false)
                onConfirm()
                GlobalDialog.setAttributes({
                    title: getTranslation('success_reactivate_membership_title', translations),
                    message: attributes?.name as string,
                    type: GlobalDialogType.SUCCESS,
                })
                GlobalDialog.open()
            }
            else {
                throw new Error(api.message)
            }
        }
        catch(err) {
            setGlobalError(err)
        }
        finally {
            setGlobalLoading(false)
        }
    }

    function onCancelModal() {
        setOpen(false)
        onCancel()
    }

    useEffect(() => {
        setOpen(modal_open)
    }, [modal_open])


    return (
        <Transition
            appear
            show={is_open}
            as="div"
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <Dialog
                as="div"
                className="fixed inset-0 z-[101] overflow-y-auto"
                onClose={onCancelModal}
            >
                <div className="min-h-screen px-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 backdrop-filter backdrop-brightness-50" />
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span
                        className="inline-block h-screen align-middle"
                        aria-hidden="true"
                    >
                        &#8203;
                    </span>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div className="inline-block text-center w-full max-w-md p-10 pt-7 pr-7 overflow-hidden align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                            <div className="text-right text-2xl cursor-pointer" onClick={onCancelModal}>
                                <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
                                    <i className="feather feather-x " />
                                </span>
                            </div>
                            <div className="mx-auto px-8 mb-5">
                                <i className="feather-alert-triangle text-xl bg-yellow-500 text-white rounded-full p-2" />
                                <div className="flex flex-col text-primary text-2xl font-medium font-display mt-10">
                                    <Translation render_as="span" content_key={is_package ? "reactivate_package" : "reactivate_subscription"} />
                                    <Translation render_as="span" content_key="please_confirm_renewal_date" />
                                </div>
                                <div className="text-primary my-5">
                                    <Translation content_key={is_package ? "renewal_date_package_question" : "renewal_date_subscription_question"} />
                                </div>
                            </div>

                            <div className="flex flex-row-reverse justify-center gap-4 mt-10">
                                <button
                                    type="button"
                                    className="bg-primary text-white rounded-lg py-3 w-1/2 focus:outline-none hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                                    onClick={() => {onConfirmModal()}}
                                >
                                    <Translation content_key="reactivate_now" />
                                </button>
                                <div className="w-1/2 relative cursor-pointer border border-gray-300 rounded-lg">
                                    <DatePickerInput
                                        className={'hidden'}
                                        minDate={new Date()}
                                        popperPlacement="top"
                                        open={show_date_picker}
                                        onChange={(
                                            date: Date
                                        ) => {
                                            onConfirmModal(date)
                                            toggleDatePicker(false)
                                        }}
                                        onInputClick={() => {
                                            toggleDatePicker(true)
                                        }}
                                        onClickOutside={() => {
                                            toggleDatePicker(false)
                                        }}
                                        disabled={true}
                                        
                                    />
                                    <div className="absolute w-full top-1"
                                        onClick={() => {
                                            toggleDatePicker(true)
                                        }}
                                    >
                                        <span className="text-black">
                                            <Translation content_key="select_date" />
                                        </span>
                                        <i className="feather-calendar text-xl text-primary-light ml-3"/>
                                    </div>
                                    
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    )
}

export default MembershipReactivateModal